import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Button, Row, Col, Statistic, Space, Typography, Tag, Empty, message, Tooltip, Modal, Alert,
} from 'antd';
import { ReloadOutlined, EyeOutlined, ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import api from '../services/api';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');
const { Text, Title } = Typography;

const CALL_TYPE_LABELS = {
  early: 'Aviso prévio',
  first: '1ª chamada',
  second: '2ª chamada',
  third: '3ª chamada',
};
const CALL_TYPE_COLORS = { early: 'blue', first: 'geekblue', second: 'orange', third: 'red' };

const ConvocationDispatch = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/monitoring/convocation-dispatch/runs', { params: { limit: 50 } });
      setRuns(res.data.runs || []);
    } catch (err) {
      message.error('Erro ao carregar execuções');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/monitoring/convocation-dispatch/runs/${row.id}`);
      setSelectedRun(res.data);
    } catch (err) {
      message.error('Erro ao carregar detalhes');
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    {
      title: 'Executado em',
      dataIndex: 'run_at',
      width: 200,
      render: (v) => (
        <Space direction="vertical" size={0}>
          <Text>{dayjs(v).format('DD/MM/YYYY HH:mm:ss')}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(v).fromNow()}</Text>
        </Space>
      ),
    },
    {
      title: 'Modo',
      dataIndex: 'mode',
      width: 100,
      render: (v) => v === 'shadow'
        ? <Tag color="default">SHADOW</Tag>
        : <Tag color="green">LIVE</Tag>,
    },
    {
      title: 'Total pendentes',
      dataIndex: 'total',
      width: 140,
      align: 'center',
      render: (v) => v > 0
        ? <Tag color={v >= 20 ? 'red' : v >= 5 ? 'orange' : 'gold'} style={{ fontSize: 14, fontWeight: 700 }}>{v}</Tag>
        : <Tag color="green">0</Tag>,
    },
    {
      title: 'Match n8n',
      dataIndex: 'by_match',
      width: 200,
      render: (v) => {
        if (!v) return <Text type="secondary">—</Text>;
        return (
          <Space wrap size={4}>
            {v.not_sent_by_n8n > 0 && <Tag color="default" style={{ fontSize: 11 }}>não env: {v.not_sent_by_n8n}</Tag>}
            {v.fully_sent_by_n8n > 0 && <Tag color="red" style={{ fontSize: 11 }}>n8n enviou: {v.fully_sent_by_n8n}</Tag>}
            {v.partial > 0 && <Tag color="orange" style={{ fontSize: 11 }}>parcial: {v.partial}</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Por tipo',
      dataIndex: 'by_type',
      render: (v) => {
        if (!v || Object.keys(v).length === 0) return <Text type="secondary">—</Text>;
        return (
          <Space wrap>
            {Object.entries(v).map(([k, n]) => (
              <Tag key={k} color={CALL_TYPE_COLORS[k]}>{CALL_TYPE_LABELS[k] || k}: {n}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Duração',
      dataIndex: 'took_ms',
      width: 100,
      align: 'right',
      render: (v) => <Text style={{ fontSize: 12 }}>{v}ms</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'error',
      width: 100,
      render: (v) => v
        ? <Tooltip title={v}><Tag color="red" icon={<WarningOutlined />}>ERRO</Tag></Tooltip>
        : <Tag color="green">OK</Tag>,
    },
    {
      title: '',
      key: 'action',
      width: 60,
      render: (_, row) => (
        <Button size="small" icon={<EyeOutlined />} loading={detailLoading} onClick={() => openDetail(row)} />
      ),
    },
  ];

  const pendingCols = [
    { title: 'Tipo', dataIndex: 'call_type', width: 100, render: (v) => <Tag color={CALL_TYPE_COLORS[v]}>{CALL_TYPE_LABELS[v] || v}</Tag> },
    {
      title: 'Time',
      dataIndex: 'game_name',
      render: (v, r) => (
        <a href={`/customers/${r.customer_id}/ficha`} target="_blank" rel="noreferrer"><Text strong>{v}</Text></a>
      ),
    },
    { title: 'Chip', dataIndex: 'instance_name', width: 120, render: (v) => <Tag>{v}</Tag> },
    {
      title: 'Agendada para',
      dataIndex: 'scheduled_at',
      width: 180,
      render: (v) => (
        <Tooltip title={dayjs(v).format('DD/MM/YYYY HH:mm')}>
          <Text style={{ fontSize: 12 }}>{dayjs(v).format('DD/MM HH:mm')}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Atrasada há',
      dataIndex: 'overdue_minutes',
      width: 120,
      align: 'right',
      render: (v) => {
        const isBad = v >= 24 * 60;
        const label = v < 60 ? `${v}min` : v < 24 * 60 ? `${Math.round(v / 60)}h` : `${Math.round(v / (24 * 60))}d`;
        return <Tag color={isBad ? 'red' : v > 60 ? 'orange' : 'default'}>{label}</Tag>;
      },
    },
    {
      title: 'Jogo em',
      dataIndex: 'game_date',
      width: 140,
      render: (v) => v
        ? <Text style={{ fontSize: 12 }}>{dayjs(v).format('DD/MM HH:mm')}</Text>
        : <Text type="secondary">—</Text>,
    },
    {
      title: (
        <Tooltip title="Quantos jogadores receberiam essa msg (respeitando player_type + dedup diário)">
          Players
        </Tooltip>
      ),
      dataIndex: 'players_count',
      width: 100,
      align: 'center',
      render: (v, r) => {
        const n = v ?? 0;
        const players = Array.isArray(r.players_to_send) ? r.players_to_send : [];
        const preview = players.slice(0, 10).map(p => `${p.name} (${p.type === '1' ? 'mensalista' : 'avulso'})`).join(', ');
        const extra = players.length > 10 ? ` +${players.length - 10}` : '';
        return n === 0
          ? <Tag color="default">0</Tag>
          : (
            <Tooltip title={preview + extra}>
              <Tag color={n >= 15 ? 'blue' : 'geekblue'}>{n}</Tag>
            </Tooltip>
          );
      },
    },
    {
      title: (
        <Tooltip title="Compara com send_message_log: se n8n JA enviou HOJE pra esse (game, call_type)">
          n8n hoje
        </Tooltip>
      ),
      dataIndex: 'match_status',
      width: 160,
      render: (v, r) => {
        const n = r.n8n_sent_count || 0;
        if (v === 'fully_sent_by_n8n') {
          return <Tooltip title={`n8n enviou pra ${n} player(s) — meu shadow lista como pendente = divergencia`}>
            <Tag color="red">n8n enviou {n}</Tag>
          </Tooltip>;
        }
        if (v === 'partial') {
          return <Tooltip title={`n8n enviou pra ${n} — mas ainda restam elegíveis pendentes`}>
            <Tag color="orange">parcial {n}</Tag>
          </Tooltip>;
        }
        return <Tag color="default">não enviou</Tag>;
      },
    },
  ];

  const latest = runs[0];
  const totalPending = latest?.total ?? 0;

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <ClockCircleOutlined /> Envio de convocações
          </Title>
          <Text type="secondary">
            Cron backend hora em hora — SHADOW MODE (só identifica, não envia). Compara contra worker n8n atual.
          </Text>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Pendentes na última execução"
              value={totalPending}
              valueStyle={{ color: totalPending > 0 ? '#faad14' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Última execução"
              value={latest ? dayjs(latest.run_at).fromNow() : '—'}
              valueStyle={{ fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Total de execuções (últimas 50)"
              value={runs.length}
            />
          </Card>
        </Col>
      </Row>

      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="Fase 1: SHADOW MODE"
        description="Este cron identifica convocações que deveriam ser enviadas, mas não dispara nada. Serve pra você comparar com o worker n8n atual antes de mudar pra envio real."
      />

      <Card>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={runs}
          columns={columns}
          size="small"
          pagination={{ pageSize: 25 }}
        />
      </Card>

      <Modal
        open={!!selectedRun}
        onCancel={() => setSelectedRun(null)}
        width={1100}
        title={selectedRun ? `Execução #${selectedRun.id} — ${dayjs(selectedRun.run_at).format('DD/MM/YYYY HH:mm:ss')}` : ''}
        footer={<Button onClick={() => setSelectedRun(null)}>Fechar</Button>}
      >
        {selectedRun && (
          <>
            {selectedRun.error && (
              <Alert type="error" showIcon message="Erro" description={selectedRun.error} style={{ marginBottom: 16 }} />
            )}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}><Statistic title="Total pendentes" value={selectedRun.total} /></Col>
              <Col span={6}><Statistic title="Duração" value={`${selectedRun.took_ms}ms`} /></Col>
              <Col span={12}>
                <Text type="secondary">Por tipo:</Text><br />
                <Space wrap>
                  {Object.entries(selectedRun.by_type || {}).map(([k, n]) => (
                    <Tag key={k} color={CALL_TYPE_COLORS[k]}>{CALL_TYPE_LABELS[k] || k}: {n}</Tag>
                  ))}
                </Space>
              </Col>
            </Row>

            <Title level={5} style={{ marginBottom: 8 }}>Pendências identificadas ({selectedRun.pending?.length || 0})</Title>
            {Array.isArray(selectedRun.pending) && selectedRun.pending.length > 0 ? (
              <Table
                rowKey={(r) => `${r.game_id}-${r.call_type}`}
                dataSource={selectedRun.pending}
                columns={pendingCols}
                size="small"
                pagination={{ pageSize: 15 }}
                scroll={{ x: 'max-content' }}
              />
            ) : (
              <Empty description="Nenhuma pendência nesta execução" />
            )}

            <Title level={5} style={{ marginTop: 24, marginBottom: 8 }}>
              Envios que o n8n fez HOJE ({selectedRun.n8n_sent_today?.length || 0} grupos)
            </Title>
            {Array.isArray(selectedRun.n8n_sent_today) && selectedRun.n8n_sent_today.length > 0 ? (
              <Table
                rowKey={(r) => `${r.game_id}-${r.dedup_type}`}
                dataSource={selectedRun.n8n_sent_today}
                columns={[
                  { title: 'Tipo', dataIndex: 'call_type', width: 100, render: (v) => <Tag color={CALL_TYPE_COLORS[v]}>{CALL_TYPE_LABELS[v] || v}</Tag> },
                  {
                    title: 'Time',
                    dataIndex: 'game_name',
                    render: (v, r) => (
                      <a href={`/customers/${r.customer_id}/ficha`} target="_blank" rel="noreferrer"><Text strong>{v}</Text></a>
                    ),
                  },
                  { title: 'Chip', dataIndex: 'instance_name', width: 120, render: (v) => v ? <Tag>{v}</Tag> : '—' },
                  { title: 'Players que receberam', dataIndex: 'sent_count', width: 160, align: 'center', render: (v) => <Tag color="green">{v}</Tag> },
                  {
                    title: '1º envio',
                    dataIndex: 'first_at',
                    width: 140,
                    render: (v) => <Text style={{ fontSize: 12 }}>{dayjs(v).format('DD/MM HH:mm:ss')}</Text>,
                  },
                  {
                    title: 'Último envio',
                    dataIndex: 'last_at',
                    width: 140,
                    render: (v) => <Text style={{ fontSize: 12 }}>{dayjs(v).format('DD/MM HH:mm:ss')}</Text>,
                  },
                ]}
                size="small"
                pagination={{ pageSize: 15 }}
                scroll={{ x: 'max-content' }}
              />
            ) : (
              <Empty description="n8n não enviou nada hoje" />
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default ConvocationDispatch;
